import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Session, SupabaseClient } from '@supabase/supabase-js';
import { showError, showInfo, confirmAction, showSuccess } from '../lib/alerts';
import { Leaderboard } from '../components/Leaderboard';
import { MetricCard } from '../components/MetricCard';
import { Topbar } from '../components/Topbar';
import { TrendChart } from '../components/TrendChart';
import { DeveloperProductivity, OverviewMetrics, TimelinePoint, WorkspaceOption } from '../types';

interface OverviewPageProps {
  supabase: SupabaseClient;
  session: Session | null;
}

function formatHours(value: number) {
  if (value === 0) return '0m';
  const hours = Math.floor(value);
  const minutes = Math.round((value - hours) * 60);
  
  if (hours === 0) {
    return `${minutes}m`;
  } else if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}m`;
}

function formatLabel(dateValue: string) {
  return new Date(dateValue).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function emptyOverview(): OverviewMetrics {
  return {
    codingHours: 0,
    editEvents: 0,
    saveEvents: 0,
    createEvents: 0,
    activeFiles: 0,
    activeDevelopers: 0,
  };
}

function userDisplayName(session: Session | null) {
  return session?.user.user_metadata?.full_name || session?.user.email || 'You';
}

export default function OverviewPage({ supabase, session }: OverviewPageProps) {
  const navigate = useNavigate();
  const [memberships, setMemberships] = useState<WorkspaceOption[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [rangeDays, setRangeDays] = useState(14);
  const [loadingMemberships, setLoadingMemberships] = useState(true);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<OverviewMetrics>(emptyOverview());
  const [timeline, setTimeline] = useState<TimelinePoint[]>([]);
  const [developers, setDevelopers] = useState<DeveloperProductivity[]>([]);

  const allProjectIds = useMemo(
    () => Array.from(new Set(memberships.map((membership) => membership.projectId).filter(Boolean))),
    [memberships]
  );
  const allWorkspaceIds = useMemo(
    () => Array.from(new Set(memberships.map((membership) => membership.workspaceId).filter(Boolean))),
    [memberships]
  );

  useEffect(() => {
    if (!session) {
      return;
    }

    const userId = session.user.id;

    let cancelled = false;

    async function loadMemberships() {
      setLoadingMemberships(true);
      setError(null);
      try {
        const { data: memberRows, error: memberError } = await supabase
          .from('project_members')
          .select('workspace_id, project_id, role')
          .eq('user_id', userId);

        if (cancelled) return;

        if (memberError) {
          showError(memberError.message);
          setError(memberError.message);
          setLoadingMemberships(false);
          return;
        }

        const projectIds = Array.from(new Set((memberRows || []).map((row: any) => row.project_id).filter(Boolean)));
        const workspaceIds = Array.from(new Set((memberRows || []).map((row: any) => row.workspace_id).filter(Boolean)));

        const projectRows: Array<{ id: string; name: string; workspace_id: string }> = [];
        const workspaceRows: Array<{ id: string; name: string }> = [];

        if (projectIds.length) {
          const { data, error: projectError } = await supabase.from('projects').select('id, name, workspace_id').in('id', projectIds);
          if (projectError) {
            showError(projectError.message);
            setError(projectError.message);
            setLoadingMemberships(false);
            return;
          }
          projectRows.push(...((data || []) as Array<{ id: string; name: string; workspace_id: string }>));
        }

        if (workspaceIds.length) {
          const { data, error: workspaceError } = await supabase.from('workspaces').select('id, name').in('id', workspaceIds);
          if (workspaceError) {
            showError(workspaceError.message);
            setError(workspaceError.message);
            setLoadingMemberships(false);
            return;
          }
          workspaceRows.push(...((data || []) as Array<{ id: string; name: string }>));
        }

        if (cancelled) return;

        const projectsById = new Map<string, { id: string; name: string; workspace_id: string }>();
        const workspacesById = new Map<string, { id: string; name: string }>();

        for (const project of projectRows) projectsById.set(project.id, project);
        for (const workspace of workspaceRows) workspacesById.set(workspace.id, workspace);

        const nextMemberships = (memberRows || []).map((row: any) => ({
          workspaceId: row.workspace_id,
          workspaceName: workspacesById.get(row.workspace_id)?.name || 'Workspace',
          projectId: row.project_id,
          projectName: projectsById.get(row.project_id)?.name || 'Project',
          role: row.role,
        })) as WorkspaceOption[];

        setMemberships(nextMemberships);
        setSelectedProjectId((current) => current || (nextMemberships.length > 0 ? 'all' : ''));
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to load memberships';
        showError(msg);
        setError(msg);
      } finally {
        setLoadingMemberships(false);
      }
    }

    void loadMemberships();

    return () => {
      cancelled = true;
    };
  }, [session, supabase]);

  useEffect(() => {
    if (!session || !selectedProjectId) {
      setOverview(emptyOverview());
      setTimeline([]);
      setDevelopers([]);
      setLoadingData(false);
      return;
    }
    let cancelled = false;

    async function loadOverview() {
      setLoadingData(true);
      setError(null);
      showInfo('Loading workspace data...');

      try {
        const selectedProjectIds = selectedProjectId === 'all' ? allProjectIds : [selectedProjectId];
        const selectedWorkspaceIds = selectedProjectId === 'all' ? allWorkspaceIds : [];
        if (!selectedProjectIds.length && !selectedWorkspaceIds.length) {
          setOverview(emptyOverview());
          setTimeline([]);
          setDevelopers([]);
          return;
        }

        const rangeStart = new Date();
        rangeStart.setDate(rangeStart.getDate() - (rangeDays - 1));
        const startDate = rangeStart.toISOString().slice(0, 10);

        const summaryQuery = supabase
          .from('daily_summaries')
          .select('summary_date, user_id, coding_seconds, edit_events, save_events, create_events, active_files')
          .gte('summary_date', startDate)
          .order('summary_date', { ascending: true });
        const memberQuery = supabase
          .from('project_members')
          .select('user_id, role, project_id')
          .order('user_id', { ascending: true });
        const activityQuery = supabase
          .from('activity_logs')
          .select('id, user_id, file_path, event_type, event_count, active_seconds, occurred_at')
          .order('occurred_at', { ascending: false })
          .limit(60);

        if (selectedProjectId === 'all') {
          summaryQuery.in('workspace_id', selectedWorkspaceIds);
          memberQuery.in('workspace_id', selectedWorkspaceIds);
          activityQuery.in('workspace_id', selectedWorkspaceIds);
        } else {
          summaryQuery.in('project_id', selectedProjectIds);
          memberQuery.in('project_id', selectedProjectIds);
          activityQuery.in('project_id', selectedProjectIds);
        }

        const { data: summaryRows, error: summaryError } = await summaryQuery;

        if (cancelled) return;
        if (summaryError) throw summaryError;

        const { data: memberRows, error: memberError } = await memberQuery;

        if (cancelled) return;
        if (memberError) throw memberError;

        const { data: logRows, error: logError } = await activityQuery;

        if (cancelled) return;
        if (logError) throw logError;

        const summaryData = summaryRows || [];
        const memberData = memberRows || [];
        const logData = logRows || [];

        const userIds = Array.from(
          new Set(
            [...memberData.map((row: any) => row.user_id), ...summaryData.map((row: any) => row.user_id), ...logData.map((row: any) => row.user_id)].filter(Boolean)
          )
        );
        const projectIdsForUsers = Array.from(new Set((memberData as any[]).map((row) => row.project_id).filter(Boolean)));

        const usersById = new Map<string, { full_name: string | null; email: string }>();
        if (userIds.length && projectIdsForUsers.length) {
          const { data: userRows } = await supabase.rpc('get_users_for_projects', {
            project_ids: projectIdsForUsers,
            user_ids: userIds,
          });
          for (const user of userRows || []) usersById.set(user.id, { full_name: user.full_name, email: user.email });
        }

        if (cancelled) return;

        const timelineMap = new Map<string, TimelinePoint>();
        const developerMap = new Map<string, DeveloperProductivity>();
        const filesTouched = new Set<string>();

        const roleRank = (role: string) => (role === 'owner' ? 3 : role === 'admin' ? 2 : 1);
        for (const member of memberData as any[]) {
          const userInfo = usersById.get(member.user_id);
          const existing = developerMap.get(member.user_id);
          const nextRole = existing && roleRank(existing.role) >= roleRank(member.role) ? existing.role : member.role;

          developerMap.set(member.user_id, {
            userId: member.user_id,
            name: userInfo?.full_name || userInfo?.email || `Developer ${developerMap.size + 1}`,
            email: userInfo?.email || 'n/a',
            role: nextRole,
            codingHours: 0,
            editEvents: 0,
            saveEvents: 0,
            createEvents: 0,
            activeDays: 0,
          });
        }

        for (const row of summaryData as any[]) {
          const existing = timelineMap.get(row.summary_date) || {
            date: row.summary_date,
            codingHours: 0,
            editEvents: 0,
            saveEvents: 0,
          };

          existing.codingHours += (row.coding_seconds || 0) / 3600;
          existing.editEvents += row.edit_events || 0;
          existing.saveEvents += row.save_events || 0;
          timelineMap.set(row.summary_date, existing);

          const developer = developerMap.get(row.user_id);
          if (developer) {
            developer.codingHours += (row.coding_seconds || 0) / 3600;
            developer.editEvents += row.edit_events || 0;
            developer.saveEvents += row.save_events || 0;
            developer.createEvents += row.create_events || 0;
            developer.activeDays += 1;
          }
        }

        for (const logRow of logData as any[]) if (logRow.file_path) filesTouched.add(logRow.file_path);

        const totalCodingSeconds = summaryData.reduce((sum: number, row: any) => sum + (row.coding_seconds || 0), 0);
        const totalEditEvents = summaryData.reduce((sum: number, row: any) => sum + (row.edit_events || 0), 0);
        const totalCreateEvents = summaryData.reduce((sum: number, row: any) => sum + (row.create_events || 0), 0);
        const totalSaveEvents = summaryData.reduce((sum: number, row: any) => sum + (row.save_events || 0), 0);
        const chartRows = Array.from(timelineMap.values()).map((row) => ({ ...row, codingHours: Number(row.codingHours.toFixed(2)) }));

        const developersWithHours = Array.from(developerMap.values())
          .map((developer) => ({ ...developer, codingHours: Number(developer.codingHours.toFixed(2)) }))
          .sort((left, right) => right.codingHours - left.codingHours);

        setOverview({
          codingHours: Number((totalCodingSeconds / 3600).toFixed(2)),
          editEvents: totalEditEvents,
          saveEvents: totalSaveEvents,
          createEvents: totalCreateEvents,
          activeFiles: filesTouched.size || summaryData.reduce((sum: number, row: any) => sum + (row.active_files || 0), 0),
          activeDevelopers: developerMap.size,
        });
        setTimeline(chartRows);
        setDevelopers(developersWithHours);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to load workspace data';
        showError(msg);
        setError(msg);
      } finally {
        setLoadingData(false);
      }
    }

    void loadOverview();

    return () => {
      cancelled = true;
    };
  }, [rangeDays, selectedProjectId, session, supabase, allProjectIds, allWorkspaceIds]);

  const selectedMembership = selectedProjectId === 'all' ? null : memberships.find((membership) => membership.projectId === selectedProjectId) || memberships[0] || null;
  const workspaceName = selectedProjectId === 'all' ? 'All teams' : selectedMembership?.workspaceName || 'Gloro Solutions';
  const projectName = selectedProjectId === 'all' ? 'All projects' : selectedMembership?.projectName || 'Guardian Green';

  const chartSeries = useMemo(
    () =>
      timeline.map((point) => ({
        label: formatLabel(point.date),
        value: point.codingHours,
      })),
    [timeline]
  );

  const codingSeries = useMemo(() => timeline.map((point) => point.codingHours), [timeline]);
  const editSeries = useMemo(() => timeline.map((point) => point.editEvents), [timeline]);
  const saveSeries = useMemo(() => timeline.map((point) => point.saveEvents), [timeline]);
  const fileSeries = useMemo(() => timeline.map((point) => Math.max(1, point.editEvents + point.saveEvents)), [timeline]);

  async function handleSignOut() {
    try {
      const result = await confirmAction('Sign Out?', 'You will be returned to the login page.');
      if (result.isConfirmed) {
        await supabase.auth.signOut();
        showSuccess('Signed out successfully');
        navigate('/login', { replace: true });
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Sign out failed');
    }
  }

  const topMetrics = [
    {
      label: 'Coding Time',
      value: formatHours(overview.codingHours),
      detail: `${developers.length || overview.activeDevelopers} active developers`,
      accent: true,
    },
    {
      label: 'File Edits',
      value: String(overview.editEvents),
      detail: `${overview.saveEvents} save events`,
      accent: false,
    },
    {
      label: 'Files Touched',
      value: String(overview.activeFiles),
      detail: `${timeline.length} days in range`,
      accent: false,
    },
    {
      label: 'Membership',
      value: String(memberships.length),
      detail: selectedProjectId === 'all' ? 'Projects in all teams' : selectedMembership ? 'Workspace members' : 'No project selected',
      accent: false,
    },
  ];

  const detailMetrics = [
    {
      label: 'Coding Time Breakdown',
      value: formatHours(overview.codingHours),
      detail: 'Rolled up from daily summaries',
      sparkValues: codingSeries,
    },
    {
      label: 'Edit Event Breakdown',
      value: String(overview.editEvents),
      detail: 'Edit activity captured by the extension',
      sparkValues: editSeries,
    },
    {
      label: 'Save Event Breakdown',
      value: String(overview.saveEvents),
      detail: 'Save events grouped by day',
      sparkValues: saveSeries,
    },
    {
      label: 'Files Touched Breakdown',
      value: String(overview.activeFiles),
      detail: 'Unique files surfaced in the activity log',
      sparkValues: fileSeries,
    },
  ];

  return (
    <div>
      <Topbar
        workspaceName={workspaceName}
        projectName={projectName}
        memberships={memberships}
        selectedProjectId={selectedProjectId}
        onProjectChange={setSelectedProjectId}
        rangeDays={rangeDays}
        onRangeChange={setRangeDays}
        onSignOut={handleSignOut}
        userName={userDisplayName(session)}
        loading={loadingMemberships}
      />

      <main className="dashboard-content">
        <section className="stack-block" id="overview">
          <div className="section-heading-row section-heading-row--spread">
            <div>
              <div className="section-kicker">OVERVIEW</div>
              <h2>Precision-engineered team telemetry.</h2>
            </div>
            {error ? <div className="error-pill error-pill--inline">{error}</div> : null}
          </div>

          <div className="metrics-grid metrics-grid--top">
            {topMetrics.map((metric) => (
              <MetricCard
                key={metric.label}
                label={metric.label}
                value={metric.value}
                detail={metric.detail}
                accented={metric.accent}
                loading={loadingMemberships || loadingData}
                size="compact"
              />
            ))}
          </div>
        </section>

        <section className="dashboard-grid" id="log-stream">
          <TrendChart data={chartSeries} loading={loadingData} />
          <div id="team-activity">
            <Leaderboard developers={developers} loading={loadingData} />
          </div>
        </section>

        <section className="stack-block" id="system-health">
          <div className="section-heading-row">
            <div>
              <div className="section-kicker">DETAILED BREAKDOWN</div>
              <h2>Secondary metrics with compact activity bars.</h2>
            </div>
          </div>
          <div className="metrics-grid metrics-grid--detail">
            {detailMetrics.map((metric) => (
              <MetricCard
                key={metric.label}
                label={metric.label}
                value={metric.value}
                detail={metric.detail}
                sparkValues={metric.sparkValues}
                loading={loadingMemberships || loadingData}
                size="large"
              />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
